import { ChevronDown } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '../../lib/utils'

export type SelectOption = {
  value: string
  label: string
}

type SelectPickerProps = {
  options: SelectOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  invalid?: boolean
}

export function SelectPicker({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  invalid = false,
}: SelectPickerProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const selectedLabel = options.find((option) => option.value === value)?.label ?? placeholder

  useEffect(() => {
    if (!open) {
      return
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  const handleSelect = (nextValue: string) => {
    onChange(nextValue)
    setOpen(false)
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className={cn(
          'flex w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-left text-sm text-ink-900 hover:bg-parch-50',
          invalid ? 'border-danger' : 'border-parch-200',
        )}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={cn('truncate', !value && 'text-ink-500')}>{selectedLabel}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-ink-400 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="absolute left-0 z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-parch-200 bg-white py-1 shadow-lg">
          {options.map((option) => {
            const isSelected = value === option.value

            return (
              <button
                key={option.value}
                type="button"
                className={cn(
                  'flex w-full px-3 py-2 text-left text-sm transition-colors',
                  isSelected ? 'bg-green-50 text-green-800' : 'text-ink-800 hover:bg-parch-50',
                )}
                onClick={() => handleSelect(option.value)}
              >
                <span className="truncate">{option.label}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
